import { CSSProperties, FunctionComponent, ReactElement, useEffect, useRef, useState } from "react";

interface StickyFooterProps {
	/**
	 * A value that tells the component how close to the bottom should the scroller be before the sticky footer hides
	 * and displays at the end of your content. The default is 0, meaning the user needs to scroll all the way to the bottom
	 * before the footer hides. A number greater than 0 would cause the sticky footer to hide at some point before the user
	 * has scrolled all the way down, depending on the value of the number.
	 */
	bottomThreshold?: number;
	/**
	 * A value that tells the component how much the user should scroll back up before the sticky footer shows up again.
	 * The default is 0.001. A number greater than the default would require the user scroll up more before the
	 * sticky footer shows up.
	 */
	stickAtThreshold?: number;
	/**
	 * Styles to be applied to the sticky footer only.
	 */
	stickyStyles?: CSSProperties;
	/**
	 * Styles to be applied to the footer in its standard location only.
	 */
	normalStyles?: CSSProperties;
	/**
	 * Children to render inside of the footer. Do not set this prop directly. Instead, set your children between the opening
	 * and closing tag of the component
	 */
	children: JSX.Element | Array<JSX.Element>;
	/**
	 * Callback that informs when the state of the footer has changed from sticky to being in normal document flow, via boolean argument.
	 * true means it is in normal flow, false means it is sticky.
	 */
	onFooterStateChange?: (isNormalFlow: boolean) => void;
}

const StickyFooter: FunctionComponent<StickyFooterProps> = (props: StickyFooterProps): ReactElement => {
	const {
		bottomThreshold = 0,
		stickAtThreshold = 0.001,
		stickyStyles,
		normalStyles,
		children,
		onFooterStateChange
	} = props;
	const [isAtBottom, setIsAtBottom] = useState<boolean>(false);
	let observer = useRef(
		new MutationObserver((mutations) => {
			determineState();
		})
	);
	const determineState = () => {
		const scrollOffset = window.pageYOffset + window.innerHeight;
		const contentHeight = document.body.clientHeight - bottomThreshold;

		if (!isAtBottom && scrollOffset >= contentHeight) {
			setIsAtBottom(true);
			onFooterStateChange && onFooterStateChange(true);
		} else if (isAtBottom && scrollOffset < contentHeight - contentHeight * stickAtThreshold) {
			setIsAtBottom(false);
			onFooterStateChange && onFooterStateChange(false);
		}
	};

	const handleScroll = () => {
		determineState();
	};

	useEffect(() => {
		observer.current.observe(document.body, {
			childList: true,
			subtree: true,
			attributes: true
		});
		window.addEventListener("scroll", handleScroll);
		determineState();

		let currentObserver = observer.current;

		return () => {
			currentObserver.disconnect();
			window.removeEventListener("scroll", handleScroll);
		};
	});

	return (
		<>
			{isAtBottom ? (
				<div style={normalStyles}>{children}</div>
			) : (
				<div style={{ ...stickyStyles, position: "fixed", bottom: 0 }}>{children}</div>
			)}
		</>
	);
};

export default StickyFooter;
